import React, { memo, useCallback, useMemo } from 'react';
import Map from '@/components/Map';
import Div from '@/shared/Div';
import Header from '@/shared/Header';
import VariantCard from '@/shared/crisp/VariantCard';
import classNames from 'classnames';

function removeDuplicatesAndCombineMaterialId(data: any) {
  const uniqueData: string[] = [];
  data?.forEach((item: any) => {
    const existingItem: any = uniqueData.find((i: any) => i?.id === item?.id);
    if (existingItem) {
      if (!Array.isArray(existingItem.materialId)) {
        existingItem.materialId = [existingItem.materialId];
      }
      existingItem.materialId.push(item?.materialId);
    } else if (item?.id) {
      uniqueData.push({ ...(item ?? {}) });
    }
  });
  return uniqueData;
}

type Variants = {
  products: any[];
  filters: any[];
  minerals: any[];
  storageTypes: any[];
  storageTanks: any[];
  tempOptions: any[];
  iotInsights: any[];
  mtdsTechnologies: any[];
  waterSavings: any[];
};

type ProductVariantsPropTypes = {
  materialId: string;
  variants: Variants;
  isSCAClient?: boolean;
};

const ProductVariants = ({ variants, materialId, isSCAClient }: ProductVariantsPropTypes) => {
  const indexOfMaterialId = variants?.products?.indexOf(materialId);
  const selectedFilter = variants?.filters?.[indexOfMaterialId];

  const getVariantCombinations = useCallback(() => {
    const selectedMineralOptions: any[] = [];
    const selectedStorageTankOptions: any[] = [];
    const selectedStorageTypeOptions: any[] = [];
    const selectedTempOptions: any[] = [];
    const selectedIotInsightOptions: any[] = [];
    const selectedMtdsTechnologies: any[] = [];
    const selectedWaterSavings: any[] = [];

    variants?.filters?.forEach((filter: any, index: number) => {
      if (filter?.id === selectedFilter?.id) {
        const commonProperties = { materialId: variants.products[index] };

        selectedMineralOptions.push({
          ...(variants?.minerals?.[index] ?? {}),
          ...commonProperties,
        });

        selectedStorageTankOptions.push({
          ...(variants?.storageTanks?.[index] ?? {}),
          ...commonProperties,
        });

        selectedStorageTypeOptions.push({
          ...(variants?.storageTypes?.[index] ?? {}),
          ...commonProperties,
        });

        selectedTempOptions.push({
          ...(variants?.tempOptions?.[index] ?? {}),
          ...commonProperties,
        });

        selectedIotInsightOptions.push({
          ...(variants?.iotInsights?.[index] ?? {}),
          ...commonProperties,
        });
        selectedMtdsTechnologies.push({
          ...(variants?.mtdsTechnologies?.[index] ?? {}),
          ...commonProperties,
        });
        selectedWaterSavings.push({
          ...(variants?.waterSavings?.[index] ?? {}),
          ...commonProperties,
        });
      }
    });

    return {
      filters: removeDuplicatesAndCombineMaterialId(variants?.filters),
      minerals: removeDuplicatesAndCombineMaterialId(selectedMineralOptions),
      storageTanks: removeDuplicatesAndCombineMaterialId(selectedStorageTankOptions),
      storageTypes: removeDuplicatesAndCombineMaterialId(selectedStorageTypeOptions),
      tempOptions: removeDuplicatesAndCombineMaterialId(selectedTempOptions),
      iotInsights: removeDuplicatesAndCombineMaterialId(selectedIotInsightOptions),
      mtdsTechnologies: removeDuplicatesAndCombineMaterialId(selectedMtdsTechnologies),
      waterSavings: removeDuplicatesAndCombineMaterialId(selectedWaterSavings),
    };
  }, [
    selectedFilter?.id,
    variants?.filters,
    variants?.iotInsights,
    variants?.minerals,
    variants?.products,
    variants?.storageTanks,
    variants?.storageTypes,
    variants?.tempOptions,
    variants?.mtdsTechnologies,
    variants?.waterSavings,
  ]);

  const {
    filters,
    minerals,
    storageTanks,
    storageTypes,
    tempOptions,
    iotInsights,
    mtdsTechnologies,
    waterSavings,
  } = useMemo(() => getVariantCombinations(), [getVariantCombinations]);

  const renderVariants = useCallback(
    (title: string, list: any[]) => {
      return (
        <Div
          className={classNames('flex-col gap-s4 lg:gap-s5 hidden ', {
            '[&:not(:first-child)]:mt-s9 [&:not(:first-child)]:lg:mt-s10 !flex': list.length > 0,
          })}
        >
          {list.length > 0 ? <Header>{title}</Header> : null}
          <Map
            list={list}
            render={(listItem) => {
              if (!listItem.id) return null;
              return (
                <VariantCard
                  key={listItem.id}
                  {...listItem}
                  isSCAClient={isSCAClient}
                  isSelected={
                    listItem.materialId === materialId || listItem?.materialId?.includes(materialId)
                  }
                />
              );
            }}
          />
        </Div>
      );
    },
    [materialId, isSCAClient],
  );

  if (!variants) return null;

  return (
    <Div className="flex flex-col mt-s9 md:mt-s10">
      {renderVariants('Purification Technology', filters)}
      {renderVariants('Mineral', minerals)}
      {renderVariants('MTDS Technology', mtdsTechnologies)}
      {renderVariants('Storage Type', storageTypes)}
      {renderVariants('Storage Tank', storageTanks)}
      {renderVariants('Temperature Option', tempOptions)}
      {renderVariants('IOT Range', iotInsights)}
      {renderVariants('Water Saving', waterSavings)}
    </Div>
  );
};

export default memo(ProductVariants);
